<template>
  <div class="generated-view" :class="{ 'is-embedded': embedded }">
    <Teleport to="body"><div v-if="museumArrival && !exhibitionOpen && !embedded" class="view-museum-arrival" role="status"><p>{{ fatalError || exhibitionError || 'Entering the exhibition' }}</p><button v-if="fatalError || exhibitionError" type="button" @click="readArticleAfterArrival">Read article <span aria-hidden="true"><ArrowUpRightIcon /></span></button><span v-else aria-hidden="true"></span></div></Teleport>
    <header v-if="!fatalError && (!shareId || !sourcesLoading)" class="view-header">
      <div class="view-meta-row">
        <div class="view-meta-stack">
          <p class="view-ai-note">
            <span class="view-ai-badge"><span aria-hidden="true">✦</span> Created with AI</span>
            <span v-if="!sourcesLoading" class="view-meta-unit">using {{ sourceCount }} {{ sourceCount === 1 ? 'source' : 'sources' }} from Henry&rsquo;s site</span>
            <span v-else class="view-meta-unit">using Henry&rsquo;s original site material</span>
          </p>
          <ViewTranslationControl
            v-if="translationAvailable"
            :loading="translationLoading"
            :visible="translationVisible"
            :loaded="hasTranslations"
            :error="translationError"
            :source="translationLanguage" :target="translationTarget"
            @select="selectTranslation"
          />
        </div>


      </div>
      <p v-if="exhibitionError" class="view-exhibition-error" role="status">{{ exhibitionError }}</p>
      <div class="view-header-copy">
        <div class="view-header-title">
          <h1 ref="viewTitle" tabindex="-1" data-scroll-reveal><ViewReadingTitle :text="view.title" article /></h1>
          <transition name="translation-reveal">
            <p v-if="translationVisible && translationFor('title')" class="view-translation-text view-title-translation" :lang="translationLang">
              <ViewReadingTitle :text="translationFor('title')" />
            </p>
          </transition>
          <div v-if="displayQuestion" class="view-question-group" data-scroll-reveal>
            <p v-handwriting="{ text: displayQuestion, language: translationLanguage }" class="view-question" :lang="translationLanguage || undefined">&ldquo;{{ displayQuestion }}&rdquo;</p>
            <transition name="translation-reveal">
              <p v-if="translationVisible && translationFor('question')" class="view-translation-text view-question-translation" :lang="translationLang">
                &ldquo;{{ translationFor('question') }}&rdquo;
              </p>
            </transition>
            <p v-if="formattedCreatedAt" class="view-question-date">
              <time :datetime="createdAtIso">{{ formattedCreatedAt }}</time>
            </p>
          </div>
        </div>
        <div class="view-header-summary">
          <p v-if="view.dek" class="view-dek" data-scroll-reveal>{{ view.dek }}</p>
          <transition name="translation-reveal">
            <p v-if="translationVisible && translationFor('summary')" class="view-translation-text view-dek-translation" :lang="translationLang">
              {{ translationFor('summary') }}
            </p>
          </transition>

          <div class="view-header-actions">
            <ViewSummaryAudio compact class="view-header-audio" v-if="view.dek" :key="view.dek" :share-id="shareId" :audio-src="view.audio?.src || ''" :language="translationVisible ? translationTarget : ''" :source-language="translationLanguage" :translation-pending="translationLoading" @audio-updated="view.audio = $event" />
          </div>
        </div>
      </div>
      <figure v-if="view.illustration && view.illustration.src" class="view-illustration" data-image-enlarge="off" data-scroll-reveal>
        <img :src="view.illustration.src" crossorigin="anonymous" :alt="view.illustration.alt || ''" loading="eager" decoding="async" draggable="false">
      </figure>
    </header>

    <transition name="view-fade">
      <aside v-if="sourceNotice" class="view-source-notice" role="status">
        <div>
          <span>This source has changed</span>
          <p>{{ sourceNotice }}</p>
        </div>
        <button type="button" aria-label="Dismiss source notice" @click="sourceNotice = ''">&times;</button>
      </aside>
    </transition>

    <div v-if="sourcesLoading" class="view-loading" role="status" aria-live="polite">
      <MorphingInfinity class="view-loading-mark" />
      <span>Preparing this <AiViewTerm />…</span>
    </div>

    <section v-else-if="fatalError" class="view-fatal" role="alert">
      <p class="view-kicker"><span aria-hidden="true">✦</span> <AiViewTerm /> unavailable</p>
      <h1>This <AiViewTerm /> could not be opened</h1>
      <p>{{ fatalError }}</p>
      <button v-if="!embedded" type="button" class="site-back-control" @click="goBack()"><span>Browse Views</span><span class="back-link-arrow" aria-hidden="true">→</span></button>
    </section>

    <article v-else class="view-sections" ref="sections">
      <transition name="view-fade" mode="out-in">
        <div :key="revision" class="view-state">
          <section
            v-for="(section, sectionIndex) in laidOutSections"
            :key="`${revision}-${sectionIndex}`"
            class="view-section"
          >
            <header v-if="section.heading" class="view-chapter-heading">
              <span class="view-chapter-number" aria-hidden="true">{{ String(sectionIndex + 1).padStart(2, '0') }}</span>
              <div>
                <h2 class="view-section-heading">{{ section.heading }}</h2>
                <transition name="translation-reveal">
                  <p v-if="translationVisible && translationFor(`section.${sectionIndex}`)" class="view-translation-text view-section-translation" :lang="translationLang">
                    {{ translationFor(`section.${sectionIndex}`) }}
                  </p>
                </transition>
              </div>
            </header>
            <div v-for="(row, rowIndex) in section.rows" :key="`${revision}-${sectionIndex}-row-${rowIndex}`" class="view-layout-row" :class="`layout-${row.type}`">
              <div
                v-for="({ block, index: blockIndex }) in row.items"
                :key="`${revision}-${sectionIndex}-${blockIndex}`"
                class="view-block"
                :data-view-position="`${sectionIndex}-${blockIndex}`"
                :class="blockClass(block)"
                :data-source-id="block.kind === 'source' ? block.source_id : null"
              >
                <ViewBridge
                  v-if="block.kind === 'bridge'"
                  :bridge="block"
                  :translated-text="translationVisible ? translationFor(`bridge.${sectionIndex}.${blockIndex}`) : ''"
                  :translation-lang="translationLang"
                />
                <ViewSourceBlock
                  v-else
                  :block="block"
                  :source="sourceFor(block.source_id)"
                  :current-source="currentSourceFor(block.source_id)"
                  @open-source="openSource"
                  @source-error="markSourceError"
                />
              </div>
            </div>
          </section>
        </div>
      </transition>
    </article>

    <p v-if="viewError && !fatalError" class="view-error" role="status">
      Some media could not be loaded. The rest of the page is still available.
    </p>

    <section v-if="closing && !sourcesLoading && !fatalError" class="view-closing" :data-view-position="`${view.sections.length}--2`">
      <h2>{{ closingLabel }}</h2>
      <p v-if="translationVisible" class="view-translation-text">{{ translatedClosingLabel }}</p>
      <p>{{ closing.text }}</p>
      <p v-if="translationVisible && translationFor('closing.text')" class="view-translation-text">{{ translationFor('closing.text') }}</p>
    </section>
    <ViewGuestbook v-if="shareId && !sourcesLoading && !fatalError && !embedded" :share-id="shareId" :title="view.title" :is-public="shareVisibility === 'public'" :can-publish="sharing.canPublish" @publish="openShareDialog('publish')" />
    <footer v-if="!sourcesLoading && !fatalError" class="view-endnote">
      <p v-if="shareNotice && !shareDialogMode && sharing.status !== 'public'" class="view-share-notice" role="status">{{ shareNotice }}</p>
      <div class="view-disclaimer">
        <span aria-hidden="true">✦</span>
        <p><strong>AI-generated</strong> This page may contain errors or omissions.</p>
      </div>
      <details v-if="translationVisible" class="view-translation-disclaimer">
        <summary>About this translation</summary>
        <p>THIS SERVICE MAY CONTAIN TRANSLATIONS POWERED BY GOOGLE. GOOGLE DISCLAIMS ALL WARRANTIES RELATED TO THE TRANSLATIONS, EXPRESS OR IMPLIED, INCLUDING ANY WARRANTIES OF ACCURACY, RELIABILITY, AND ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.</p>
      </details>
      <section v-if="viewSources.length" :data-view-position="`${view.sections.length}--3`" class="view-citations" aria-labelledby="view-citations-title">
        <div class="view-citations-heading">
          <h2 id="view-citations-title">Citations</h2>
          <p>Original material used to compose this <AiViewTerm /></p>
        </div>
        <ol>
          <li v-for="(source, index) in viewSources" :key="source.source_id">
            <button type="button" @click="openSource(source)">
              <span class="view-citation-number">{{ String(index + 1).padStart(2, '0') }}</span>
              <span class="view-citation-copy">
                <span>{{ sourcePageLabel(source) }}</span>
                <strong>{{ citationTitle(source) }}</strong>
              </span>
              <ArrowUpRightIcon class="view-citation-arrow" />
            </button>
          </li>
        </ol>
      </section>
    </footer>
    <ViewShareDialog
      v-if="shareDialogMode && !sourcesLoading && !fatalError"
      :mode="shareDialogMode"
      :view-title="view.title"
      :url="shareUrl"
      :sharing="sharing"
      :publishing="publishing"
      :notice="shareNotice"
      @close="closeShareDialog"
      @publish="publishView"
      @retry="ensurePermanentLink()"
    />
    <Teleport to="body">
      <Transition name="view-exhibition-invitation">
        <ExhibitionInvitation v-if="!exhibitionOpen && !embedded && !fatalError && !sourcesLoading && view.sections?.length" :view="view" :sources="sourcesById" :share-id="shareId" :created-at="createdAt" :language="translationLanguage" :get-position="exhibitionPositionAtScroll" :loading="exhibitionLoading" @enter="openExhibition" />
      </Transition>
    </Teleport>
    <component ref="exhibition" :is="ExhibitionComponent" :showcase="showcaseWalkthrough" :showcase-linked="showcaseLinkedJourney" :showcase-loop="$route.query.showcaseLoop === '1'" :showcase-return-hall="$route.query.showcaseReturn === '1'" :showcase-controls="showcaseControls" :showcase-channel="showcaseChannel" :showcase-frame="$route.query.galleryFrame === '1'" :created-at="createdAt" :share-id="shareId" :is-public="shareVisibility === 'public'" :can-publish="sharing.canPublish" @published="onExhibitionPublished" @publish="openShareDialog('publish')" :translations="translations" :translation-available="translationAvailable" :translation-source="translationLanguage" :translation-target="translationTarget" :translation-visible="translationVisible" :translation-loading="translationLoading" :translation-error="translationError" @select-translation="selectTranslation" v-if="exhibitionOpen && ExhibitionComponent" :view="view" :sources="sourcesById" :current-sources="currentSourcesById" :initial-position="exhibitionPosition" @leave-start="prepareExhibitionReturn" @close="closeExhibition" />
  </div>
</template>

<script>
import { revealViewTransition } from '../utils/viewPresentationTransition';
import { takePreparedMuseumVisit } from '../utils/prepareMuseumVisit';
import { cleanViewClosing, viewClosingLabel } from '../utils/viewClosing';
import { prefersMuseum, museumReturnPosition, updateMuseumSession } from '../utils/viewMuseumSession';
import exhibitionGuides from '../data/exhibitionGuides.json';
import ViewGuestbook from './view/ViewGuestbook.vue';
import ExhibitionInvitation from './view/ExhibitionInvitation.vue';
import { markRaw } from 'vue';
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import {
  consumePendingViewReturnScroll,
  loadAskSession,
  setAskShare,
} from '../utils/askSession';
import { createViewCatalog } from '../utils/viewCatalog.js';
import ViewBridge from './view/ViewBridge.vue';
import ViewSummaryAudio from './view/ViewSummaryAudio.vue';
import ViewReadingTitle from './view/ViewReadingTitle.vue';
import ViewShareDialog from './view/ViewShareDialog.vue';
import ViewTranslationControl from './view/ViewTranslationControl.vue';
import ViewSourceBlock from './view/ViewSourceBlock.vue';
import AiViewTerm from './ui/AiViewTerm.vue';
import MorphingInfinity from './ui/MorphingInfinity.vue';
import { updatePageActivity, activityEnabled } from '../utils/siteActivity';
import { logEvent } from '../utils/askTelemetry';
import { validateViewState } from '../utils/viewValidation';
import {
  createSharedView,
  getSharedView,
  getViewTranslationStatus,
  publishSharedView,
  translateView,
} from '../utils/viewShareSite';
import { sourceFingerprint } from '../utils/viewSnapshotCompare';
import { sourceDisplayTitle } from '../utils/askRetrieval';
import { sourcePageLabel, viewSourceTarget } from '../utils/viewSourceTarget';
import { viewSharingState } from '../utils/viewSharingState';
import { layoutViewSection } from '../utils/viewLayout';
import { systemViewLanguage, sameViewLanguage } from '../utils/viewLanguages';
import { viewTranslationSession, chooseViewLanguage, loadViewTranslation } from '../utils/viewTranslationSession';



export default {
  name: 'GeneratedViewPage',
  emits: ['page-title'],
  components: { ViewGuestbook, ExhibitionInvitation, AiViewTerm, MorphingInfinity, ViewBridge, ViewSourceBlock, ViewSummaryAudio, ViewReadingTitle, ViewTranslationControl, ViewShareDialog },
  mixins: [scrollRevealMixin],
  data() {
    return {
      view: {
        schema_version: 1,
        question: '',
        lens: '',
        title: 'Composed View',
        dek: '',
        sections: [],
      },
      revision: 0,
      session: null,
      sourcesById: new Map(),
      currentSourcesById: new Map(),
      viewError: false,
      fatalError: '',
      sourcesLoading: true,
      shareId: '',
      shareOwner: false,
      shareVisibility: 'unlisted',
      shareLoading: false,
      publishing: false,
      shareDialogMode: '',
      shareNotice: '',
      sourceNotice: '',
      createdAt: null,
      translationChecked: false,
      translationLanguage: '',
      translationPreference: viewTranslationSession,
      translationTarget: viewTranslationSession.language || systemViewLanguage(navigator.languages),
      translationLoading: false,
      translationVisible: false,
      translations: {},
      translationError: '',
      ExhibitionComponent: null,
      exhibitionOpen: false,
      museumArrival: false,
      exhibitionLoading: false,
      exhibitionError: '',
      exhibitionPosition: null,
    };
  },
  computed: {
    showcaseWalkthrough() { return this.$route.query.showcase === 'walkthrough' && this.$route.query.galleryFrame === '1'; },
    showcaseLinkedJourney() { return this.$route.query.showcase === 'linkedJourney' && this.$route.query.galleryFrame === '1'; },
    showcaseArticleJourney() { return this.$route.query.showcase === 'articleJourney' && this.$route.query.galleryFrame === '1'; },
    showcaseChannel() { return String(this.$route.query.showcaseChannel || ''); },
    showcaseControls() { return this.$route.query.showcaseControls === '1'; },
    temporaryPresentation() { return this.$route.query.temporaryPresentation === '1'; },
    closingLabel() { return viewClosingLabel(this.translationLanguage, this.closing?.text); },
    translatedClosingLabel() { return viewClosingLabel(this.translationTarget, '', this.translationFor('closing.label')); },
    closing() { const guide = exhibitionGuides[this.shareId]; return cleanViewClosing(this.view.closing || (guide?.question === this.view.question ? guide.closing : null)); },
    laidOutSections() {
      return (this.view.sections || []).map(section => ({
        ...section,
        rows: layoutViewSection(section, id => this.sourceFor(id)),
      }));
    },
    embedded() {
      return String(this.$route.query.embedded || '') === '1';
    },
    sourceCount() {
      const ids = new Set();
      for (const section of this.view.sections || []) {
        for (const block of section.blocks || []) {
          if (block.kind === 'source' && block.source_id) ids.add(block.source_id);
        }
      }
      return ids.size;
    },
    viewSources() {
      const seen = new Set();
      const sources = [];
      for (const section of this.view.sections || []) {
        for (const block of section.blocks || []) {
          if (block.kind !== 'source' || !block.source_id || seen.has(block.source_id)) continue;
          const source = this.sourceFor(block.source_id);
          if (!source) continue;
          seen.add(block.source_id);
          sources.push(source);
        }
      }
      return sources;
    },
    displayQuestion() {
      const saved = String(this.view.question || '').trim();
      if (saved) return saved;
      if (this.shareId) return '';
      const turns = this.session && Array.isArray(this.session.turns) ? this.session.turns : [];
      const latestUserTurn = [...turns].reverse().find((turn) => turn.role === 'user');
      return latestUserTurn ? String(latestUserTurn.text || '').trim() : '';
    },
    sharing() {
      return viewSharingState(this);
    },
    shareUrl() {
      return this.shareId ? `${window.location.origin}${window.location.pathname}#/view/${this.shareId}` : '';
    },
    formattedCreatedAt() {
      if (!this.createdAt) return '';
      const date = new Date(this.createdAt);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    },
    createdAtIso() {
      if (!this.createdAt) return '';
      const date = new Date(this.createdAt);
      return Number.isNaN(date.getTime()) ? '' : date.toISOString();
    },
    translationAvailable() {
      return !!this.shareId && !this.sourcesLoading;
    },
    hasTranslations() {
      return Object.keys(this.translations).length > 0;
    },
    translationLang() { return this.translationTarget; },
  },
  created() {
    this._routeLoadToken = 0;
    this._translationToken = 0;
  },
  beforeMount() { this.museumArrival=this.showcaseWalkthrough||this.showcaseLinkedJourney||(!this.embedded && window.self===window.top && (this.$route.query.presentation==='museum'||prefersMuseum()) && this.$route.query.presentation!=='article'); },
  async mounted() {
    if(this.showcaseArticleJourney)this._articleShowcasePaused=true;
    window.addEventListener('message', this.onShowcasePlayback);
    await this.loadRouteView();
  },
  beforeUnmount() { this._routeLoadToken += 1; this._translationToken += 1; clearTimeout(this._articleShowcaseTimer); window.removeEventListener('message', this.onShowcasePlayback); },
  watch: {
    'translationPreference.language'(language) {
      if (!this.sourcesLoading && this.shareId) this.applyReadingLanguage(language);
    },
    '$route.fullPath'() {
      const nextShareId = String(this.$route.params.shareId || '');
      if (nextShareId === this.shareId) return;
      this.loadRouteView();
    },
  },
  methods: {
    updatePageTitle() {
      document.title = `${this.view.title} - H.W.`;
      this.$emit('page-title', { path: this.$route.path, title: this.view.title });
    },
    startArticleShowcase() {
      clearTimeout(this._articleShowcaseTimer);
      if (!this.showcaseArticleJourney || !this.showcaseChannel) return;
      const positioned = [...this.$el.querySelectorAll('[data-view-position]')];
      const targets = [this.$el.querySelector('.view-header'), ...positioned].filter(Boolean);
      if (!targets.length) return;
      let index = 0;
      this._articleShowcaseVisit = () => {
        if (!this.showcaseArticleJourney || !this.$el?.isConnected || this._articleShowcasePaused) return;
        const target = targets[index];
        const match = target.dataset.viewPosition?.match(/^(\d+)-(-?\d+)$/);
        const position = match ? { sectionIndex: Number(match[1]), blockIndex: Number(match[2]) } : null;
        const top = index === 0 ? 0 : window.scrollY + target.getBoundingClientRect().top - Math.max(28, window.innerHeight * .16);
        window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        window.parent.postMessage({
          type: 'view-article-showcase-position',
          channel: this.showcaseChannel,
          position,
        }, window.location.origin);
        index = (index + 1) % targets.length;
        this._articleShowcaseTimer = setTimeout(this._articleShowcaseVisit, index === 0 ? 3000 : 2350);
      };
      window.parent.postMessage({type:'view-gallery-showcase-ready',channel:this.showcaseChannel},location.origin);
      if (!this._articleShowcasePaused) this._articleShowcaseTimer = setTimeout(this._articleShowcaseVisit, 500);
    },
    onShowcasePlayback(event) {
      if ((!this.showcaseArticleJourney && !this.showcaseWalkthrough && !this.showcaseLinkedJourney) || event.origin !== window.location.origin || event.source !== window.parent) return;
      const message = event.data;
      if (message?.type !== 'view-gallery-showcase-playback' || message.channel !== this.showcaseChannel) return;
      this._articleShowcasePaused = Boolean(message.paused);
      if (this._articleShowcasePaused) clearTimeout(this._articleShowcaseTimer);
      else if (this.showcaseArticleJourney && this._articleShowcaseVisit) this._articleShowcaseTimer = setTimeout(this._articleShowcaseVisit, 120);
    },
    readArticleAfterArrival() { this.museumArrival=false;updatePageActivity({presentation:'article',place:''});updateMuseumSession({mode:'article',resumeOnViews:false}); },
    async prepareExhibition() {
      if (this._exhibitionPreparation) return this._exhibitionPreparation;
      this._exhibitionPreparation = Promise.all([
        import(/* webpackChunkName: "view-exhibition" */ './view/ViewExhibition.vue'),
        import(/* webpackChunkName: "view-exhibition-scene" */ '../utils/exhibitionScene'),
      ]).then(([component]) => { this.ExhibitionComponent = markRaw(component.default); }).catch(() => { this._exhibitionPreparation = null; });
      return this._exhibitionPreparation;
    },
    exhibitionPositionAtScroll() {
      const blocks = [...(this.$el.querySelectorAll('[data-view-position]') || [])];
      const nearest = blocks.filter(element => element.getBoundingClientRect().bottom > 110)
        .sort((a,b) => Math.abs(a.getBoundingClientRect().top - 160) - Math.abs(b.getBoundingClientRect().top - 160))[0];
      const position = nearest?.dataset.viewPosition.match(/^(\d+)-(-?\d+)$/);
      return position && nearest.getBoundingClientRect().top < window.innerHeight * .65 && window.scrollY > 300
        ? { sectionIndex: Number(position[1]), blockIndex: Number(position[2]) } : null;
    },
    async openExhibition(event) {
      if (this.exhibitionLoading || this.sourcesLoading || this.exhibitionOpen) return;
      const token = this._routeLoadToken;
      if(!this.showcaseWalkthrough&&!this.showcaseLinkedJourney&&!this.temporaryPresentation)updateMuseumSession({mode:'museum',resumeOnViews:false});
      this.exhibitionLoading = true;
      this.exhibitionError = '';
      this._exhibitionScroll = window.scrollY;
      this.exhibitionPosition = event?.resume ? museumReturnPosition(this.shareId) : this.exhibitionPositionAtScroll();
      try {
        await this.prepareExhibition();
        if (!this.ExhibitionComponent) throw new Error('Exhibition unavailable');
        if (token === this._routeLoadToken) this.exhibitionOpen = true;
      } catch {
        this.exhibitionError = 'The exhibition could not open. Please try again.';
        this.museumArrival=false;revealViewTransition();
      } finally { this.exhibitionLoading = false; }
    },
    prepareExhibitionReturn(position) {
      const element = position && this.$el.querySelector(`[data-view-position="${position.sectionIndex}-${position.blockIndex}"]`);
      const top = element ? window.scrollY + element.getBoundingClientRect().top - 112 : this._exhibitionScroll || 0;
      window.scrollTo({ top, behavior: 'instant' });
    },
    closeExhibition(position, navigation = {target:'article'}) {
      if(navigation.target !== 'article') {
        const showcaseReturn=this.showcaseWalkthrough&&this.$route.query.showcaseReturn==='1'&&navigation.target==='hall';
        const target=showcaseReturn?{path:'/views',query:{showcase:'exhibition',galleryFrame:'1',showcaseLoop:this.$route.query.showcaseLoop||'1',showcaseControls:this.$route.query.showcaseControls||'1',showcaseView:this.$route.query.showcaseView||this.shareId,showcaseCycle:this.$route.query.showcaseCycle||'1',showcaseChannel:this.showcaseChannel}}:navigation.target==='site'?navigation.path:'/views';
        this.$router.push(target).then(() => { if(navigation.target==='site')setTimeout(revealViewTransition,420); });return;
      }
      this.museumArrival = false;
      this.exhibitionOpen = false;
      updatePageActivity({presentation:'article',place:''});
      updateMuseumSession({mode:'article'});
      this.$nextTick(() => {
        const element = position && this.$el.querySelector(`[data-view-position="${position.sectionIndex}-${position.blockIndex}"]`);
        if (element) {
          window.scrollTo({ top: window.scrollY + element.getBoundingClientRect().top - 112, behavior: 'auto' });
          element.setAttribute('tabindex', '-1'); element.focus({ preventScroll: true });
        } else window.scrollTo({ top: this._exhibitionScroll || 0, behavior: 'auto' });
      });
    },
    resetRouteView() {
      clearTimeout(this._articleShowcaseTimer);
      this.$emit('page-title', { path: this.$route.path, title: '' });
      this.museumArrival=!this.embedded && window.self===window.top && prefersMuseum() && this.$route.query.presentation!=='article';
      this.exhibitionOpen = false;
      this.exhibitionPosition = null;
      this.exhibitionError = '';
      this.view = {
        schema_version: 1,
        question: '',
        lens: '',
        title: 'Composed View',
        dek: '',
        sections: [],
      };
      this.sourcesById = new Map();
      this.currentSourcesById = new Map();
      this.viewError = false;
      this.fatalError = '';
      this.sourcesLoading = true;
      this.shareOwner = false;
      this.shareVisibility = 'unlisted';
      this.shareLoading = false;
      this.publishing = false;
      this.shareDialogMode = '';
      this.shareNotice = '';
      this.sourceNotice = '';
      this.createdAt = null;
      this.translationChecked = false;
      this.translationLanguage = '';
      this.translationLoading = false;
      this.translationVisible = false;
      this.translations = {};
      this.translationError = '';
      this._translationToken += 1;
    },
    async loadRouteView() {
      const token = ++this._routeLoadToken;
      const requestedShareId = String(this.$route.params.shareId || '');
      this.resetRouteView();
      this.session = loadAskSession();
      this.shareId = requestedShareId;

      if (requestedShareId) {
        await this.restoreSharedView(token, requestedShareId);
        return;
      }

      const currentView = this.session?.currentView;
      if (!currentView) {
        this.goBack(true);
        return;
      }
      this.view = currentView;
      this.createdAt = this.session?.currentShare?.created_at || Date.now();
      const loaded = await this.loadSources();
      if (token !== this._routeLoadToken) return;
      if (!loaded) {
        this.sourcesLoading = false;
        this.fatalError = 'Henry’s original site material is unavailable. Please return and try again.';
        return;
      }
      const validationError = validateViewState(currentView, this.sourcesById);
      if (validationError) {
        this.sourcesLoading = false;
        this.fatalError = 'Some of the original material has changed, so this saved View cannot be opened completely.';
        return;
      }
      this.shareOwner = true;
      this.finishViewLoad();
      await this.ensurePermanentLink(token);
    },
    finishViewLoad() {
      this.sourcesLoading = false;
      this.revision = 1;
      this.updatePageTitle();
      const pendingScroll = consumePendingViewReturnScroll() ?? window.history.state?.scroll?.top ?? null;
      if (pendingScroll !== null) {
        // Both the return control and browser history restore after the saved
        // View has loaded, when its full reading height is available again.
        this.$nextTick(() => window.scrollTo({ top: pendingScroll, left: 0, behavior: 'auto' }));
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
      updatePageActivity({ title: this.view.title, share_id: this.shareId, presentation: this.museumArrival ? 'page' : 'article', place: this.museumArrival ? 'Opening exhibition' : '' });
      if (!this.embedded && activityEnabled()) logEvent('view_open', {
        share_id: this.shareId,
        n_sections: this.view.sections ? this.view.sections.length : 0,
        n_sources: this.view.sections ? this.view.sections.reduce((sum, section) => sum + (section.blocks || []).filter((b) => b.kind === 'source').length, 0) : 0,
      });
      this.$nextTick(() => {
        this.refreshScrollReveal();
        this.$refs.viewTitle?.focus({ preventScroll: true });
        if (this.showcaseArticleJourney) this.startArticleShowcase();
      });
      if (this.shareId) this.restoreReadingLanguage();
      const routePresentation=this.$route?.query?.presentation;
      if(this.showcaseWalkthrough || this.showcaseLinkedJourney || (!this.embedded && window.self===window.top && (routePresentation==='museum'||prefersMuseum()) && routePresentation!=='article')) this.$nextTick(()=>this.openExhibition({resume:!this.showcaseWalkthrough&&!this.showcaseLinkedJourney}));
    },
    async restoreSharedView(token = this._routeLoadToken, requestedShareId = this.shareId) {
      try {
        const prepared = takePreparedMuseumVisit(requestedShareId);
        const snapshot = prepared?.snapshot || await getSharedView(requestedShareId);
        if (token !== this._routeLoadToken || requestedShareId !== this.shareId) return;
        this.view = snapshot.view;
        this.sourcesById = new Map((snapshot.sources || []).map((source) => [source.source_id, source]));
        if (prepared && !prepared.sourceIndex) this.currentSourcesById = new Map();
        else await this.loadCurrentSources(prepared?.sourceIndex);
        if (token !== this._routeLoadToken || requestedShareId !== this.shareId) return;
        for (const section of this.view.sections || []) {
          for (const block of section.blocks || []) {
            if (block.kind !== 'bridge') continue;
            for (const sourceId of block.source_ids || []) {
              if (!this.sourcesById.has(sourceId)) {
                this.sourcesById.set(sourceId, { source_id: sourceId, parts: [], presentations: [] });
              }
            }
          }
        }
        this.shareOwner = snapshot.owner === true;
        this.shareVisibility = snapshot.visibility || 'unlisted';
        this.createdAt = snapshot.created_at || null;
        const validationError = validateViewState(this.view, this.sourcesById);
        if (validationError) throw new Error(validationError);
        this.finishViewLoad();
      } catch (error) {
        if (token !== this._routeLoadToken || requestedShareId !== this.shareId) return;
        console.error('shared view restore failed:', error);
        this.sourcesLoading = false;
        this.fatalError = 'This saved View is unavailable or incomplete.';
        revealViewTransition();
      }
    },
    async ensurePermanentLink(token = this._routeLoadToken) {
      if (!this.shareOwner || this.shareLoading) return;
      this.shareNotice = '';
      const existing = this.session?.currentShare;
      if (existing?.id) {
        if (token !== this._routeLoadToken) return;
        this.shareId = existing.id;
        this.shareOwner = true;
        this.shareVisibility = existing.visibility || 'unlisted';
        this.createdAt = existing.created_at || null;
        await this.$router.replace(`/view/${existing.id}`).catch(() => {});
        if (token === this._routeLoadToken) this.updatePageTitle();
        if (token === this._routeLoadToken) this.restoreReadingLanguage();
        return;
      }
      this.shareLoading = true;
      try {
        const share = await createSharedView(this.view, await this.snapshotSources());
        if (token !== this._routeLoadToken) return;
        this.shareId = share.id;
        this.shareOwner = true;
        this.shareVisibility = share.visibility;
        this.createdAt = share.created_at || Date.now();
        this.session = setAskShare(this.session, share);
        await this.$router.replace(`/view/${share.id}`).catch(() => {});
        if (token === this._routeLoadToken) this.updatePageTitle();
        if (token === this._routeLoadToken) this.restoreReadingLanguage();
      } catch (error) {
        if (token !== this._routeLoadToken) return;
        console.error('permanent view save failed:', error);
        this.shareNotice = 'The shareable link is temporarily unavailable. Please try again.';
      } finally {
        if (token === this._routeLoadToken) this.shareLoading = false;
      }
    },
    async translationRequest(method, id, token) {
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const result = method === 'POST'
          ? await loadViewTranslation(id, this.translationTarget, translateView)
          : await getViewTranslationStatus(id, this.translationTarget);
        if (token !== this._translationToken || id !== this.shareId) return null;
        if (result.status !== 'preparing') return result;
        await new Promise(resolve => window.setTimeout(resolve, 900 + attempt * 120));
      }
      throw new Error('translation_preparing');
    },
    applyTranslationResult(result) {
      if (!result) return;
      this.translationLanguage = String(result.sourceLanguage || '');
      this.translationChecked = !!this.translationLanguage;
      if (result.translations && typeof result.translations === 'object') this.translations = result.translations;
    },
    restoreReadingLanguage() {
      if (this.translationPreference.language) return this.applyReadingLanguage(this.translationPreference.language);
      return this.detectViewLanguage();
    },
    async detectViewLanguage() {
      const id = this.shareId;
      if (!id || this.translationChecked || this.translationLoading) return;
      const token = ++this._translationToken;
      this.translationLoading = true;
      try {
        const result = await this.translationRequest('GET', id, token);
        if (result) this.applyTranslationResult(result);
      } catch (error) {
        if (token !== this._translationToken || id !== this.shareId) return;
        console.warn('View language detection unavailable:', error);
        // Keep the existing View unchanged when the provider cannot identify
        // its language. A failed control would offer no useful next action.
        this.translationLanguage = '';
        this.translationChecked = false;
      } finally {
        if (token === this._translationToken && id === this.shareId) this.translationLoading = false;
      }
    },
    selectTranslation(target) {
      const previous = this.translationPreference.language;
      const language = chooseViewLanguage(target);
      if (previous === language) return this.applyReadingLanguage(language);
    },
    async applyReadingLanguage(target) {
      if (!target || sameViewLanguage(target, this.translationLanguage)) { ++this._translationToken; this.translationLoading = false; this.translationVisible = false; this.translationError = ''; return; }
      if (target === this.translationTarget && this.hasTranslations) { this.translationVisible = true; return; }
      this.translationTarget = target; this.translations = {}; this.translationVisible = false;
      const id = this.shareId;
      if (!id) return;
      const token = ++this._translationToken;
      this.translationLoading = true; this.translationError = '';
      try {
        const result = await this.translationRequest('POST', id, token);
        if (!result) return;
        this.applyTranslationResult(result);
        if (sameViewLanguage(this.translationLanguage, target)) return;
        if (!this.hasTranslations) throw new Error('translation_unavailable');
        this.translationVisible = true;
      } catch (error) {
        if (token !== this._translationToken || id !== this.shareId) return;
        this.translationError = error?.message === 'translation_preparing'
          ? 'The translation is still being prepared. Try again in a moment.'
          : 'Translation is temporarily unavailable. Please try again.';
      } finally {
        if (token === this._translationToken && id === this.shareId) this.translationLoading = false;
      }
    },
    translationFor(id) {
      const value = this.translations?.[id];
      return typeof value === 'string' ? value : String(value?.text || '');
    },
    async snapshotSources() {
      await this.$nextTick();
      const htmlById = new Map();
      for (const element of this.$el.querySelectorAll('.view-block[data-source-id]')) {
        const sourceId = element.dataset.sourceId;
        const source = this.sourceFor(sourceId);
        if (!source?.rich) continue;
        const content = element.querySelector('.origin-content');
        if (!content) continue;
        const clone = content.cloneNode(true);
        for (const node of clone.querySelectorAll('[src], [poster]')) {
          for (const attribute of ['src', 'poster']) {
            const value = node.getAttribute(attribute);
            if (!value) continue;
            try {
              node.setAttribute(attribute, new URL(value, 'https://www.henryw.me').toString());
            } catch {
              // Keep the original value if it cannot be resolved.
            }
          }
        }
        for (const node of clone.querySelectorAll('button, input, select, textarea')) {
          node.setAttribute('disabled', 'disabled');
          node.setAttribute('aria-disabled', 'true');
        }
        htmlById.set(sourceId, clone.innerHTML);
      }
      const sourceIds = new Set();
      for (const section of this.view.sections || []) {
        for (const block of section.blocks || []) {
          if (block.kind === 'source' && block.source_id) sourceIds.add(block.source_id);
          if (block.kind === 'bridge') {
            for (const sourceId of block.source_ids || []) sourceIds.add(sourceId);
          }
        }
      }
      return Promise.all([...sourceIds].map((sourceId) => this.sourceFor(sourceId)).filter(Boolean).map(async (source) => {
        const snapshot = JSON.parse(JSON.stringify(source));
        snapshot.source_fingerprint = await sourceFingerprint(source);
        snapshot.snapshot_html = htmlById.get(source.source_id) || '';
        snapshot.media = (snapshot.media || []).map((media) => {
          const next = { ...media };
          for (const field of ['src', 'fullSrc']) {
            if (!next[field]) continue;
            try { next[field] = new URL(next[field], 'https://www.henryw.me').toString(); } catch { /* keep source */ }
          }
          return next;
        });
        return snapshot;
      }));
    },
    openShareDialog(mode) {
      if (mode === 'publish' && !this.sharing.canPublish) return;
      this.shareDialogMode = mode;
    },
    closeShareDialog() {
      this.shareDialogMode = '';
    },
    onExhibitionPublished() {
      this.shareVisibility = 'public';
      if (this.session?.currentShare?.id === this.shareId) this.session = setAskShare(this.session, { ...this.session.currentShare, visibility: 'public' });
    },
    async publishView() {
      if (!this.sharing.canPublish) return;
      this.publishing = true;
      this.shareNotice = '';
      try {
        const result = await publishSharedView(this.shareId);
        this.shareVisibility = 'public';
        if (this.session?.currentShare?.id === this.shareId) this.session = setAskShare(this.session, { ...this.session.currentShare, visibility: 'public' });
        this.shareNotice = result.featured === false
          ? 'This View is public and can be found through search in Views.'
          : 'Visitors can now find this View in Views.';
      } catch (error) {
        console.error('view publish failed:', error);
        this.shareNotice = 'This View could not be added to Views. Please try again.';
      } finally {
        this.publishing = false;
      }
    },
    citationTitle(source) {
      const title = String(source?.title || source?.page || 'Original source');
      const organisation = String(source?.entity?.organisation || '').trim();
      return organisation && !title.includes(organisation) ? `${title} · ${organisation}` : title;
    },
    blockClass(block) {
      if (block && block.kind === 'source') {
        return [`prominence-${block.prominence || 'standard'}`, `presentation-${block.presentation || 'excerpt'}`];
      }
      return ['prominence-standard', 'presentation-bridge'];
    },
    async loadSources() {
      try {
        const map = await this.fetchCurrentSourceMap();
        this.sourcesById = map;
        this.currentSourcesById = map;
        return true;
      } catch (err) {
        console.error('view index load failed:', err);
        return false;
      }
    },
    async loadCurrentSources(preparedIndex) {
      try {
        this.currentSourcesById = await this.fetchCurrentSourceMap(preparedIndex);
      } catch (error) {
        console.warn('current source comparison unavailable:', error);
        this.currentSourcesById = new Map();
      }
    },
    async fetchCurrentSourceMap(preparedIndex) {
      let data = preparedIndex;
      if (!data) {
        const res = await fetch('/assets/view-index.json', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`view-index.json ${res.status}`);
        data = await res.json();
      }
      const map = new Map(createViewCatalog(data).map((source) => [
        source.source_id, { ...source, title: sourceDisplayTitle(source) },
      ]));
      return map;
    },
    currentSourceFor(id) {
      return this.currentSourcesById.get(id) || null;
    },
    sourceFor(sourceId) {
      if (this.sourcesById.has(sourceId)) return this.sourcesById.get(sourceId);
      return null;
    },
    markSourceError() {
      this.viewError = true;
    },
    openSource(source) {
      if (!source || !source.route) return;
      const target = viewSourceTarget(source);
      if (!target) return;
      this.sourceNotice = '';
      logEvent('view_source_open', {
        source_id: source.source_id || null,
        route: target.path || target.href,
        anchor: source.anchor || null,
      });
      if (target.href) {
        window.open(target.href, '_blank', 'noopener');
        return;
      }
      if (!this.$router.resolve(target).matched.length) {
        this.sourceNotice = 'The original page has since been removed from Henry’s site. This View keeps the source as it appeared when it was created.';
        return;
      }
      target.state = {
        viewReturnRoute: this.$route.fullPath,
        viewReturnScrollY: window.scrollY,
      };
      this.$router.push(target).catch(() => {});
    },
    sourcePageLabel,
    goBack(replace = false) {
      updateMuseumSession({mode:'article',resumeOnViews:false});
      this.$router[replace ? 'replace' : 'push']('/views').catch(() => {});
    },
  },
};
</script>

<style scoped>
.view-museum-arrival { position:fixed;inset:0;z-index:30000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;background:#f5f3ee;color:#78716a;font:14px 'Quattrocento Sans',sans-serif; }
:global(html.dark-mode .view-museum-arrival), :global(body.dark-mode .view-museum-arrival) { background:#292623;color:#bdb6ac; }
.view-museum-arrival span { width:90px;height:1px;background:currentColor;animation:view-arrival-line 1.4s ease-in-out infinite; }
.view-museum-arrival button { border:0;background:none;color:inherit;font:inherit;cursor:pointer; }
@keyframes view-arrival-line { 50% {opacity:.25;transform:scaleX(.5);} }
@media(prefers-reduced-motion:reduce) { .view-museum-arrival span {animation:none;} }
.view-exhibition-error { font-size: 13px; color: var(--site-text-secondary); }
.view-exhibition-invitation-enter-active, .view-exhibition-invitation-leave-active { transition: opacity 180ms ease, transform 180ms cubic-bezier(.23, 1, .32, 1); }
.view-exhibition-invitation-enter-from, .view-exhibition-invitation-leave-to { opacity: 0; transform: translateX(8px); }
@media (prefers-reduced-motion: reduce) { .view-exhibition-invitation-enter-active, .view-exhibition-invitation-leave-active { transition: none; } }

.view-closing { max-width: 43rem; margin: 5rem auto 2.5rem; padding-top: 2rem; border-top: 1px solid var(--view-line); }
.view-closing .view-closing-label { font: 11px/1.4 'Quattrocento Sans',sans-serif; letter-spacing: .06em; color: var(--site-text-tertiary); margin: 0 0 14px; }
.view-closing h2 { font: 26px/1.3 'PT Serif',Georgia,serif; margin: 0 0 20px; }
.view-closing > p:not(.view-closing-label) { font: 17px/1.8 'PT Serif',Georgia,serif; }
.generated-view {
  --view-ink: #2f2f2f;
  --view-muted: #767676;
  --view-line: #e7e1da;
  --view-wash: rgba(250, 247, 242, 0.74);
  --view-accent: var(--site-accent);
}

.view-header {
  margin-bottom: 2rem;
}

.view-dek {
  margin: 0 0 1rem;
  font-family: "Courier Prime", monospace;
  font-size: 1.05rem;
  line-height: 1.65;
  color: var(--view-muted);
}
.view-translation-text {
  padding-left: 0.9rem;
  border-left: 1px solid color-mix(in srgb, var(--view-accent) 46%, transparent);
  color: color-mix(in srgb, var(--view-muted) 88%, transparent);
  font-family: "Quattrocento Sans", sans-serif;
  text-wrap: pretty;
}
.view-title-translation {
  --reading-subtitle-size: 1em;
  --reading-subtitle-color: inherit;
  max-width: 38rem;
  margin: -0.08rem 0 0.9rem;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.5;
}
.view-question-translation {
  max-width: 38rem;
  margin: 0.38rem 0 0;
  font-size: 0.78rem;
  line-height: 1.55;
}
.view-dek-translation {
  max-width: none;
  margin: 0.62rem 0 0;
  font-size: 0.93rem;
  line-height: 1.65;
}
.view-section-translation {
  max-width: 34rem;
  margin: -1rem 0 1.5rem;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.5;
}
.translation-reveal-enter-active,
.translation-reveal-leave-active {
  transition: opacity 180ms ease, transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.translation-reveal-enter-from,
.translation-reveal-leave-to { opacity: 0; transform: translateY(-0.25rem); }

.view-illustration {
  width: 100%;
  margin: 1.35rem 0 0;
  overflow: hidden;
  background: var(--view-wash);
}

.view-illustration img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 1;
  object-fit: cover;
}

.view-sections {
  margin-bottom: 2rem;
}

.view-section + .view-section {
  margin-top: 2rem;
}

.view-section-heading {
  margin: 0 0 1rem;
  font-family: "Courier Prime", monospace;
  font-size: 1.35rem;
  font-weight: 400;
  line-height: 1.3;
}

.view-block {
  margin: 1.25rem 0;
}

.view-block.prominence-feature {
  margin: 1.6rem 0;
}

.view-block.prominence-compact {
  margin: 0.7rem 0;
}

.view-source-missing {
  padding: 0.8rem 1rem;
  border-left: 3px solid #b3893d;
  background: rgba(0, 0, 0, 0.03);
  color: var(--view-muted);
}

.view-quote {
  margin: 0;
  padding: 0.15rem 0 0.15rem 1.25rem;
  border-left: 0.18rem solid var(--view-accent);
}

.view-quote p {
  margin: 0.45rem 0;
  font-size: 1.06rem;
  line-height: 1.7;
  color: var(--view-ink);
}

.view-excerpt p {
  margin: 0.5rem 0;
  line-height: 1.72;
  color: #4f4f4f;
}

.view-card {
  padding: 1rem;
  border: 1px solid var(--view-line);
  border-radius: 10px;
  background: var(--view-wash);
}

.view-card-kicker {
  margin: 0 0 0.35rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--view-muted);
}

.view-card-title {
  margin: 0 0 0.45rem;
  font-size: 1.08rem;
}

.view-card-meta {
  margin: 0 0 0.45rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  color: var(--view-muted);
}

.view-card-desc {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #5c5c5c;
}

.view-card-media img {
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  margin-top: 0.8rem;
  border: 1px solid var(--view-line);
  border-radius: 6px;
  background: #fff;
}

.view-media {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

.view-media img {
  width: 100%;
  border: 1px solid var(--view-line);
  border-radius: 8px;
  background: #fff;
}

.view-provenance {
  margin-top: 0.65rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  color: var(--view-muted);
}

.view-provenance button {
  appearance: none;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 0 0 2px;
  cursor: pointer;
}

.view-error {
  color: var(--view-muted);
  font-size: 0.86rem;
  padding: 0.8rem 0;
  border-top: 1px solid var(--view-line);
}

.view-fade-enter-active,
.view-fade-leave-active {
  transition: opacity 0.24s ease, transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}
.view-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.view-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

body.dark-mode .generated-view {
  --view-ink: #d7d7d7;
  --view-muted: var(--site-text-tertiary);
  --view-line: #2e2e2e;
  --view-wash: rgba(var(--site-accent-shade-rgb), 0.55);
  --view-accent: var(--site-accent);
}

body.dark-mode .view-excerpt p { color: var(--site-text-secondary); }
body.dark-mode .view-card-desc { color: var(--site-text-secondary); }
body.dark-mode .view-translation-text,
body.dark-mode .view-question-date { color: var(--site-text-tertiary); }
body.dark-mode .view-card-media img,
body.dark-mode .view-media img { background: #141414; }
body.dark-mode .view-source-missing { background: rgba(255, 255, 255, 0.04); }

@media (prefers-reduced-motion: reduce) {
  .view-fade-enter-active,
  .view-fade-leave-active {
    transition: opacity 0.1s ease;
  }
  .view-fade-enter-from,
  .view-fade-leave-to {
    transform: none;
  }
}

/* Composed-page surface */
.generated-view {
  width: 100%;
  max-width: var(--site-content-width);
  margin-inline: auto;
  padding-bottom: 1rem;
  container-type: inline-size;
  font-optical-sizing: auto;
}
.generated-view.is-embedded {
  padding-top: 0.85rem;
}
.view-header {
  max-width: none;
  margin-bottom: 2.4rem;
}
.view-meta-row,
.view-ai-note,
.view-question,
.view-question-date {
  font-family: "Quattrocento Sans", sans-serif;
}
.view-meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: clamp(1.7rem, 4vw, 2.5rem);
}
.view-meta-stack { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 18px; }
.view-ai-note {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.28rem;
  max-width: 35rem;
  margin: 0;
  color: var(--view-muted);
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: 0.018em;
}
.view-meta-unit {
  display: inline-flex;
  align-items: baseline;
  gap: 0.2rem;
}
.view-ai-badge {
  color: var(--view-accent);
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}
.view-header h1,
.view-fatal h1 {
  margin: 0 0 0.65rem;
  max-width: none;
  font-family: "PT Serif", Georgia, serif;
  font-size: clamp(34px, 4.8cqw, 48px);
  font-weight: 700;
  line-height: 1.17;
  letter-spacing: -0.025em;
  outline: none;
}
.view-question {
  max-width: 38rem;
  margin: 0;
  color: var(--view-muted);
  font-family: "La Belle Aurore", "LXGW WenKai TC", cursive;
  font-size: 1.15rem;
  line-height: 1.7;
  letter-spacing: 0.012em;
  text-wrap: pretty;
}
.view-question-group {
  max-width: 38rem;
  margin-top: 0.85rem;
}
.view-question-date {
  display: block;
  margin-top: 0.24rem;
  color: color-mix(in srgb, var(--view-muted) 78%, transparent);
  font-size: 0.64rem;
  line-height: 1.4;
  letter-spacing: 0.018em;
  font-variant-numeric: tabular-nums;
}
.view-question-date time { white-space: nowrap; }
.view-dek {
  max-width: none;
  margin: 0;
  color: var(--view-ink);
  font-family: "PT Serif", Georgia, serif;
  font-size: 16px;
  line-height: 1.8;
  text-wrap: pretty;
}
.view-section {
  position: relative;
  padding-top: 28px;
  border-top: 1px solid var(--view-line);
}
.view-section + .view-section {
  margin-top: clamp(56px, 7cqw, 80px);
}
.view-chapter-heading { margin-bottom: 28px; }
.view-chapter-number { display: block; margin-bottom: 10px; color: var(--view-muted); font: 11px/1.5 "Quattrocento Sans", sans-serif; letter-spacing: .08em; }
.view-section-heading {
  max-width: 36ch;
  margin: 0;
  font-family: "PT Serif", Georgia, serif;
  font-size: clamp(25px, 3.2cqw, 32px);
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: -0.015em;
}
.view-header-copy { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: clamp(38px, 6cqw, 76px); align-items: start; }
.view-header-title, .view-header-summary { min-width: 0; }
.view-header-summary { min-width: 0; padding-top: 5px; }
.view-header-summary .view-dek { color: color-mix(in srgb, var(--view-ink) 82%, var(--view-muted)); font-size: clamp(16px, 1.8cqw, 18px); line-height: 1.8; }
.view-header-actions { display: flex; align-items: flex-start; padding-top: 27px; }
.view-header-audio { flex: 0 1 300px; min-width: 0; }
@media (max-width: 767px) { .view-header-actions { padding-top: 23px; } }

.view-illustration { position: relative; overflow: hidden; margin-top: 28px; border-radius: 0; cursor: var(--site-default-cursor); }
.view-illustration img { transition: transform 650ms cubic-bezier(.22,1,.36,1); cursor: inherit; }
@media (hover: hover) and (pointer: fine) {
  .view-illustration:hover img { transform: scale(1.025); }
}
@media (prefers-reduced-motion: reduce) { .view-illustration img { transition: none; } .view-illustration:hover img { transform: none; } }
.view-layout-row { min-width: 0; margin-top: 28px; }
.view-layout-row:first-of-type { margin-top: 0; }
.layout-reading { max-width: 46rem; }
.layout-paired { display: grid; grid-template-columns: minmax(0, 4fr) minmax(0, 6fr); gap: 40px; align-items: start; margin-block: 36px; }
.view-block,
.view-block.prominence-feature,
.view-block.prominence-compact { width: 100%; min-width: 0; margin: 0; }
.view-block :deep(.view-bridge) { margin: 0; }
.view-block :deep(.view-bridge-translation) { margin: 16px 0 0; }
.view-block :deep(.view-source),
.view-block :deep(.view-origin-frame),
.view-block :deep(.view-rich-block),
.view-block :deep(.view-citation) { margin-block: 0; }
.view-block :deep(.origin-content > *) { zoom: 1; }
.layout-wide :deep(.origin-content) { padding: 24px; }
.layout-paired :deep(.origin-content) { padding: 24px; }
.view-block :deep(.original-block .fragment-card) { background: transparent; }
.view-block :deep(.original-block .fragment-summary) { padding: 0; cursor: var(--site-interactive-cursor); }
.view-block :deep(.original-block .fragment-summary h2) { font-size: 20px; font-weight: 400; line-height: 1.35; }
.view-block :deep(.original-block .fragment-body) { padding: 0; }
.view-block :deep(.citation-source),
.view-block :deep(.view-provenance button) { cursor: var(--site-interactive-cursor); }
.view-section-translation { margin: 12px 0 0; }
@container (max-width: 760px) {
  .view-header-copy, .layout-paired { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .view-header-summary { max-width: 44rem; }
  .layout-wide :deep(.origin-content), .layout-paired :deep(.origin-content) { padding: 18px; }
}
.view-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 13rem;
  color: var(--view-muted);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
}
.view-source-notice {
  position: fixed;
  right: clamp(1rem, 3vw, 2rem);
  bottom: clamp(1rem, 3vw, 2rem);
  z-index: 40;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: min(calc(100vw - 2rem), 22rem);
  padding: 0.9rem 1rem;
  background: #edf1ef;
  color: var(--view-ink);
  font-family: "Quattrocento Sans", sans-serif;
}
.view-source-notice span {
  color: var(--view-accent);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.view-source-notice p { margin: 0.28rem 0 0; color: var(--view-muted); font-size: 0.7rem; line-height: 1.5; }
.view-source-notice button {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--view-muted);
  font-size: 1rem;
  cursor: pointer;
}
.view-loading-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  color: var(--view-accent);
  background: rgba(var(--site-accent-rgb), 0.1);
  animation: view-breathe 1.5s ease-in-out infinite;
}
.view-fatal {
  max-width: 38rem;
  padding: clamp(2rem, 6vw, 3.5rem) 0;
}
.view-fatal p:not(.view-kicker) {
  max-width: 34rem;
  margin: 1.2rem 0;
  color: var(--view-muted);
  line-height: 1.65;
}
.view-endnote {
  margin-top: clamp(4rem, 10vw, 7rem);
  color: var(--view-muted);
  font-family: "Quattrocento Sans", sans-serif;
}
.view-share-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem 1.5rem;
  margin-bottom: 1.4rem;
}
.view-share-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 40px;
  margin-inline-start: auto;
  margin-inline-end: -0.55rem;
  padding: 0.45rem 0.55rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--view-muted);
  font: 500 0.8rem/1.5 "Quattrocento Sans", sans-serif;
  cursor: var(--site-interactive-cursor);
  transition: background-color 140ms ease, color 140ms ease, transform 140ms ease;
}
.view-share-trigger > svg, .view-publish-trigger > svg, .view-public-status > svg { flex-shrink: 0; width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; }
.view-publish-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 40px;
  padding: 0.45rem 0.65rem;
  border: 1px solid var(--view-line);
  border-radius: 6px;
  background: transparent;
  color: var(--view-ink);
  font: 500 0.8rem/1.5 "Quattrocento Sans", sans-serif;
  cursor: var(--site-interactive-cursor);
  transition: background-color 140ms ease, border-color 140ms ease, transform 140ms ease;
}
.view-publish-trigger:disabled { opacity: .5; cursor: var(--site-default-cursor); }
.view-public-status { display: inline-flex; align-items: center; gap: 0.45rem; min-height: 40px; color: var(--view-muted); font-size: 0.75rem; }
.view-public-status > svg { color: var(--view-accent); }
.view-share-notice { margin: -0.6rem 0 1.4rem; font-size: 0.72rem; }
.view-share-trigger:focus-visible, .view-publish-trigger:focus-visible { outline: 1px solid var(--view-accent); outline-offset: 2px; }
.view-share-trigger:active, .view-publish-trigger:active:not(:disabled) { transform: scale(.98); }
@media (hover: hover) and (pointer: fine) {
  .view-share-trigger:hover { color: var(--view-ink); background: rgba(var(--site-accent-rgb), 0.07); }
  .view-publish-trigger:hover:not(:disabled) { border-color: var(--view-accent); background: rgba(var(--site-accent-rgb), 0.05); }
}
.view-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.9rem 0;
  border-top: 1px solid var(--view-line);
  color: var(--view-muted);
  font-size: 0.72rem;
  line-height: 1.5;
}
.view-disclaimer > span { color: var(--view-accent); }
.view-disclaimer p { margin: 0; }
.view-disclaimer strong {
  color: var(--view-ink);
  font-weight: 600;
}
.view-translation-disclaimer {
  margin-top: 0.7rem;
  color: var(--view-muted);
  font-size: 0.62rem;
  line-height: 1.5;
}
.view-translation-disclaimer summary {
  width: fit-content;
  cursor: pointer;
  text-underline-offset: 2px;
}
.view-translation-disclaimer p {
  max-width: 40rem;
  margin: 0.55rem 0 0;
}
.view-citations {
  margin-top: clamp(2.8rem, 7vw, 4.5rem);
}
.view-citations-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}
.view-citations-heading h2 {
  margin: 0;
  color: var(--view-ink);
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.012em;
}
.view-citations-heading p {
  margin: 0;
  font-size: 0.68rem;
}
.view-citations ol {
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--view-line);
}
.view-citations li {
  border-bottom: 1px solid var(--view-line);
}
.view-citations button {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  min-height: 3.8rem;
  padding: 0.62rem 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.view-citation-number {
  color: var(--view-accent);
  font-size: 0.66rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
}
.view-citation-copy,
.view-citation-copy > span,
.view-citation-copy strong {
  display: block;
}
.view-citation-copy > span {
  margin-bottom: 0.12rem;
  color: var(--view-muted);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.view-citation-copy strong {
  overflow: hidden;
  color: var(--view-ink);
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.view-citation-arrow {
  color: var(--view-accent);
  transition: transform 160ms ease;
}
.view-citations button:hover .view-citation-arrow,
.view-citations button:focus-visible .view-citation-arrow {
  transform: translate(2px, -2px);
}
.view-citations button:focus-visible {
  outline: 1px solid currentColor;
  outline-offset: 4px;
}
.view-citations button:active { transform: scale(0.995); }
@keyframes view-breathe {
  0%, 100% { opacity: 0.5; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1); }
}
@media (max-width: 600px) {
  .view-header { margin-bottom: 2.4rem; }
  .view-header h1,
  .view-fatal h1 { font-size: 30px; }
  .view-section-heading { font-size: 25px; }
  .view-section + .view-section { margin-top: 2.9rem; }
  .view-citations-heading {
    display: block;
  }
  .view-citations-heading p { margin-top: 0.28rem; }
  .view-meta-row { flex-wrap: wrap; }
}
@media (prefers-reduced-motion: reduce) {
  .view-loading-mark { animation: none; opacity: 1; }
  .view-citation-arrow { transition: none; }
  .view-citations button:hover .view-citation-arrow,
  .view-citations button:focus-visible .view-citation-arrow { transform: none; }
  .view-publish-trigger,
  .view-share-trigger,
  .translation-reveal-enter-active,
  .translation-reveal-leave-active { transition-duration: 0.01ms; }
  .translation-reveal-enter-from,
  .translation-reveal-leave-to { transform: none; }
}

:global(body.dark-mode .view-source-notice) { background: var(--site-accent-surface); }
</style>
